import type { BodyHandle } from 'box3d-web';
import { BLAST_MIN_LAUNCH } from '../../services/damageModel';
import { invariantSnapshot, runInvariantStep } from '../../services/invariantMonitor';
import type { Vec3 } from '../../services/fracture/voronoiFracture';
import { copyReleaseSample } from './events';
import { computePileRadius, liveFragmentVolume } from './comminutionBridge';
import { bodyPositionLive } from './debrisPool';
import {
  DEFAULT_WEAPON_POWER,
  MAX_DYNAMIC_CHUNKS,
  MAX_IMPACT_TRACES,
  RELEASED_DISPLACED_EPS,
} from './demolitionTuning';
import type {
  DemoTool,
  DemolitionRuntime,
  ImpactTrace,
} from './types';
import type { ResolveResult } from '../../services/damageModel';

export function publishLastResolve(rt: DemolitionRuntime, result: ResolveResult) {
  rt.lastResolve = { ...result.stats, pulverizedVolume: rt.lastPulverizedVolume };
  rt.clampEngagements += result.stats.clampedChunks;
  if (import.meta.env.DEV && result.stats.clampedChunks > 0) {
    // eslint-disable-next-line no-console
    console.log(
      `[demolition] launch clamp engaged chunks=${result.stats.clampedChunks} ` +
        `maxSpeed=${result.stats.maxChunkSpeed.toFixed(2)}`,
    );
  }
  if (import.meta.env.DEV && result.stats.flooredChunks > 0) {
    // eslint-disable-next-line no-console
    console.log(
        `[demolition] min-launch floor engaged chunks=${result.stats.flooredChunks} ` +
        `min=${BLAST_MIN_LAUNCH} maxSpeed=${result.stats.maxChunkSpeed.toFixed(2)}`,
    );
  }
}

export function cellTransformOffset(rt: DemolitionRuntime, cellId: number): number | null {
  const rec = rt.records.get(cellId);
  if (!rec || rec.bufferIndex < 0) return null;
  // bufferIndex was set at rebuild time to the position in ids array
  const idx = rt.bodyIndex.get(rec.bodyId);
  if (idx == null) return null;
  return idx * 7;
}

/** Deterministic DEV/e2e-only spin used by the rest-space visual proof. */
export function spinDynamicChunk(
  rt: DemolitionRuntime,
  restPoint: Vec3 = { x: 0, y: 0, z: rt.wallHalf.z },
): number | null {
  let selected: { cellId: number; bodyId: BodyHandle; distance: number } | null = null;
  for (const cell of rt.cells) {
    const record = rt.records.get(cell.id);
    if (!record || record.state !== 'dynamic' || !record.bodyId) continue;
    const dx = cell.centroid.x - restPoint.x;
    const dy = cell.centroid.y - restPoint.y;
    const dz = cell.centroid.z - restPoint.z;
    const distance = dx * dx + dy * dy + dz * dz;
    if (!selected || distance < selected.distance) {
      selected = { cellId: cell.id, bodyId: record.bodyId as BodyHandle, distance };
    }
  }
  if (!selected) return null;
  try {
    rt.world.setAngularVelocity(selected.bodyId, [1.8, 3.1, 0.9]);
    rt.world.wakeBody(selected.bodyId);
  } catch {
    return null;
  }
  return selected.cellId;
}

export function cannonballTransformOffset(rt: DemolitionRuntime, index: number): number {
  // Cannonballs are last in the buffer.
  const base = rt.bodyIds.length - rt.cannonballs.length;
  return (base + index) * 7;
}

export function demolitionBodyCounts(rt: DemolitionRuntime) {
  let dynamic = rt.cannonballs.length;
  let fading = 0;
  for (const rec of rt.records.values()) {
    if (!rec.bodyId) continue;
    if (rec.state === 'dynamic') dynamic += 1;
    else if (rec.state === 'removed') fading += 1;
  }
  return {
    live: rt.bodyCountTotal,
    static: rt.staticBodyCount,
    dynamic,
    fading,
  };
}

export function snapshotImpactTraces(rt: DemolitionRuntime): ImpactTrace[] {
  return rt.eventRing
    .recent()
    .filter((event) => event.kind === 'impact-trace')
    .slice(-MAX_IMPACT_TRACES)
    .map((event) => {
      const payload = event.payload as unknown as Omit<ImpactTrace, 'id' | 'sequence'>;
      return {
        id: event.sequence,
        epoch: event.epoch,
        sequence: event.sequence,
        ...payload,
        contactPoint: { ...payload.contactPoint },
        damageOrigin: { ...payload.damageOrigin },
        directReleases: payload.directReleases.map(copyReleaseSample),
        supportReleases: payload.supportReleases.map(copyReleaseSample),
      };
    });
}

export function projectileToolAt(rt: DemolitionRuntime, index: number): DemoTool {
  return rt.projectileWeapons.get(rt.cannonballs[index]) ?? 'cannon';
}

export function projectilePowerAt(rt: DemolitionRuntime, index: number): number {
  return rt.projectilePowers.get(rt.cannonballs[index]) ?? DEFAULT_WEAPON_POWER;
}

/** Snapshot for window.__demolitionState. */
export function getDemolitionSnapshot(rt: DemolitionRuntime) {
  const inv = invariantSnapshot(rt.invariantMon);
  const bodyCounts = demolitionBodyCounts(rt);
  // Firing-corridor telemetry: rubble parked at shot height in front of the
  // wall is what turns repeated impacts into a dead cushion — make that pile
  // geometry observable in one number instead of requiring screenshots.
  let dynCorridorCount = 0;
  let dynYSum = 0;
  let dynCount = 0;
  let releasedDisplacedCount = 0;
  const displacedEps2 = RELEASED_DISPLACED_EPS * RELEASED_DISPLACED_EPS;
  for (const rec of rt.records.values()) {
    if (rec.state !== 'dynamic') continue;
    const off = cellTransformOffset(rt, rec.cellId);
    if (off == null) continue;
    const x = rt.transforms[off];
    const y = rt.transforms[off + 1];
    const z = rt.transforms[off + 2];
    dynYSum += y;
    dynCount += 1;
    if (Math.abs(x) < 1.2 && y > 1.0 && z > rt.wallHalf.z && z < 3.5) {
      dynCorridorCount += 1;
    }
    const dx = x - rec.worldCentroid.x;
    const dy = y - rec.worldCentroid.y;
    const dz = z - rec.worldCentroid.z;
    if (dx * dx + dy * dy + dz * dz > displacedEps2) {
      releasedDisplacedCount += 1;
    }
  }
  // Live projectile poses: where each ball actually is/ends up is the single
  // most contested diagnostic when repeated impacts misbehave.
  const projectilePositions: Array<{ x: number; y: number; z: number; speed: number }> = [];
  for (const ballId of rt.cannonballs) {
    const pos = bodyPositionLive(rt, ballId);
    if (!pos) continue;
    let speed = 0;
    try {
      const v = rt.world.getLinearVelocity(ballId as BodyHandle);
      speed = Math.hypot(v.x, v.y, v.z);
    } catch {
      speed = 0;
    }
    projectilePositions.push({ x: pos.x, y: pos.y, z: pos.z, speed });
  }
  return {
    /**
     * Accumulated fixed-step simulation time (s). Probes/E2E MUST gate on this
     * rather than wall-clock waits: under software GL the sim can run at a
     * small fraction of real time and wall-clock assertions measure nothing.
     */
    simTime: rt.stepper.simTime,
    /** Dynamic chunks hovering in the firing corridor (|x|<1.2, y>1, z in front of wall). */
    dynCorridorCount,
    /** Mean height of dynamic chunks — a settled field reads well under ~0.6. */
    dynMeanY: dynCount > 0 ? dynYSum / dynCount : 0,
    /** Dynamic chunks whose live position is > RELEASED_DISPLACED_EPS from spawn centroid. */
    releasedDisplacedCount,
    /** releasedDisplacedCount / live dynamic record count (0 when none). */
    releasedDisplacedFrac: dynCount > 0 ? releasedDisplacedCount / dynCount : 0,
    /** Spikes currently stuck in structure (kinematic until TTL or un-embed). */
    embeddedProjectiles: rt.embeddedProjectiles.size,
    projectilePositions,
    loaded: true,
    /** Active material profile id. */
    material: rt.profile.id,
    /**
     * Intact: pristine partition size.
     * Fractured: staticCells + dynamicChunks + removedChunks (F4 identity).
     */
    cellCount: rt.baseCellCount,
    staticCells: rt.staticCells,
    dynamicChunks: rt.dynamicChunks,
    /**
     * Culled (asleep dyn-cap eviction) + shard-degraded (spawn-time dyn-cap
     * or pure size-floor subdivide). Part of F4 bookkeeping identity.
     */
    removedChunks: rt.removedChunks,
    awake: rt.awakeCount,
    fractured: rt.fractured,
    lastImpact: rt.lastImpact ? { ...rt.lastImpact } : null,
    collapsedUnsupported: rt.collapsedUnsupported,
    wallVersion: rt.wallVersion,
    maxDynamicY: rt.maxDynamicY,
    fadingChunks: rt.fadingChunks,
    subChunks: rt.subChunks,
    shardParticles: rt.shardParticles,
    minChunkVol: rt.minChunkVol,
    maxChunkVol: rt.maxChunkVol,
    lastCarveMs: rt.lastCarveMs,
    lastPartitionMs: rt.lastPartitionMs,
    tool: rt.tool,
    weaponPower: rt.weaponPower,
    projectileCount: rt.cannonballs.length,
    charges: rt.charges.filter((c) => !c.detonated).length,
    preCrackedCells: rt.preCrackedCells,
    lastBlast: rt.lastBlast ? { ...rt.lastBlast } : null,
    /** DEV: invariant violation count + ring buffer (empty / 0 in prod). */
    invariantViolations: inv.count,
    invariantViolationList: inv.violations,
    invariantOverheadMsPerStep: inv.overheadMsPerStep,
    stepMs: rt.stepMs,
    bodyCountTotal: bodyCounts.live,
    staticBodyCount: bodyCounts.static,
    dynamicBodyCount: bodyCounts.dynamic,
    fadingBodyCount: bodyCounts.fading,
    remnantMergeMs: rt.remnantMergeMs,
    remnantMergeCount: rt.remnantMergeCount,
    eventRingCapacity: rt.eventRing.capacity,
    eventRingHistory: rt.eventRing.recent().length,
    eventRingTelemetry: rt.eventRing.telemetry(),
    damageLedgerCapacity: rt.damageLedger.capacity,
    damageLedgerHistory: rt.damageLedger.facts.length,
    damageLedgerTelemetry: rt.damageLedger.telemetry(),
    damageFacts: rt.damageLedger.facts,
    maxLiveCells: rt.maxLiveCells,
    liveCells: rt.cells.length,
    pristineCellCount: rt.pristineCellCount,
    lastResolve: rt.lastResolve ? { ...rt.lastResolve } : null,
    pulverizedVolume: rt.lastPulverizedVolume,
    sessionPulverizedVolume: rt.sessionPulverizedVolume,
    lastShardVolume: rt.lastShardVolume,
    lastSpawnedChunkVolume: rt.lastSpawnedChunkVolume,
    residueGeneration: rt.residueGeneration,
    fragmentVolumes: rt.fragmentVolumes.slice(0, 300),
    clampEngagements: rt.clampEngagements,
    patchEngagements: { ...rt.patchEngagements },
    pileRadius: computePileRadius(rt),
    pileHeight: rt.maxDynamicY,
    /** Bounded recent kinetic-contact traces (max 8); derived from the canonical ring. */
    impactTraces: snapshotImpactTraces(rt),
    /** Per-dynamic-cell sleep flags (0 asleep); same length as cellBodyIds. */
    cellSleepStates: rt.cellSleepStates,
    /** Count of nonzero entries in cellSleepStates (cheap FX cull hint). */
    awakeCells: countAwakeCells(rt.cellSleepStates),
  };
}

export function countAwakeCells(sleepStates: Uint8Array): number {
  let awake = 0;
  for (let i = 0; i < sleepStates.length; i += 1) {
    if (sleepStates[i] !== 0) awake += 1;
  }
  return awake;
}

export function runDemolitionInvariants(rt: DemolitionRuntime) {
  if (!import.meta.env.DEV || !rt.invariantMon) return;
  const sleepStates = rt.cellSleepStates;
  const dynIds = rt.cellBodyIds;
  runInvariantStep({
    mon: rt.invariantMon,
    simTime: rt.stepper.simTime,
    transforms: rt.transforms,
    bodyIds: rt.bodyIds,
    isCannonball: (id) => rt.cannonballSet.has(id),
    getSpeed: (bodyId) => {
      try {
        const v = rt.world.getLinearVelocity(bodyId as BodyHandle);
        return Math.hypot(v.x, v.y, v.z);
      } catch {
        return null;
      }
    },
    isAwake: (bodyId) => {
      // Only dynamic rubble is subject to never-sleeps; statics/balls skip.
      const idx = dynIds.indexOf(bodyId);
      if (idx < 0) return false;
      if (sleepStates.length > idx) {
        return sleepStates[idx] !== 0;
      }
      return true;
    },
    cellCount: rt.baseCellCount,
    staticCells: rt.staticCells,
    dynamicChunks: rt.dynamicChunks,
    removedChunks: rt.removedChunks,
    maxLiveCells: rt.maxLiveCells,
    liveCellCount: rt.cells.length,
    maxDynamicChunks: MAX_DYNAMIC_CHUNKS,
    fractured: rt.fractured,
    pristineCellCount: rt.pristineCellCount,
    liveVolume: liveFragmentVolume(rt),
    carveEvent: null,
  });
}

/** Report live shard particle count from the page VFX into the runtime bridge. */
export function setShardParticleCount(rt: DemolitionRuntime, count: number) {
  rt.shardParticles = Math.max(0, count | 0);
}
